import { formatElapsed } from './timeAgo';
import type { TimelineEvent } from '@shared/types';

function formatTime(ts: string) {
  return new Date(ts).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function getDotColor(event: string): string {
  const lower = event.toLowerCase();
  if (lower.includes('completed') || lower.includes('settled') || lower.includes('verified') || lower.includes('captured')) return 'bg-emerald-400';
  if (lower.includes('failed')) return 'bg-red-400';
  if (lower.includes('expired') || lower.includes('abandoned')) return 'bg-zinc-400';
  if (lower.includes('refund')) return 'bg-purple-400';
  if (lower.includes('disputed') || lower.includes('dispute')) return 'bg-orange-400';
  if (lower.includes('hold')) return 'bg-orange-400';
  if (lower.includes('authorized')) return 'bg-blue-400';
  if (lower.includes('payment') || lower.includes('received') || lower.includes('forwarding')) return 'bg-sky-400';
  if (lower.includes('initiated') || lower.includes('checkout') || lower.includes('redirect')) return 'bg-sky-400';
  return '';
}

function isNeutralDot(event: string): boolean {
  return getDotColor(event) === '';
}

const EVENT_LABELS: Record<string, string> = {
  order_expired: 'Checkout abandoned',
};

export default function OrderTimeline({ events }: { events: TimelineEvent[] }) {
  if (events.length === 0) {
    return (
      <p
        className="font-sans text-sm"
        style={{ color: 'var(--dash-text-dim)' }}
      >
        No timeline events yet.
      </p>
    );
  }

  return (
    <div className="relative pl-6">
      <div
        className="absolute left-2 top-1 bottom-1 w-px"
        style={{ background: 'var(--dash-border)' }}
      />
      {events.map((event, i) => {
        const prevTs = i > 0 ? new Date(events[i - 1].timestamp).getTime() : null;
        const curTs = new Date(event.timestamp).getTime();
        const elapsed = prevTs !== null ? curTs - prevTs : null;
        const displayLabel = EVENT_LABELS[event.event] || event.event;
        const dotClass = getDotColor(displayLabel);
        const neutral = isNeutralDot(displayLabel);

        return (
          <div key={i} className="relative mb-4 last:mb-0">
            <div
              className={`absolute -left-4 top-1.5 w-2 h-2 rounded-full ${dotClass}`}
              style={{
                borderWidth: 2,
                borderStyle: 'solid',
                borderColor: 'var(--dash-bg)',
                ...(neutral ? { background: 'var(--dash-text-muted)' } : {}),
              }}
            />
            <div
              className="font-sans text-sm font-medium"
              style={{ color: 'var(--dash-text-secondary)' }}
            >
              {displayLabel}
            </div>
            <div className="flex items-center gap-3 mt-0.5">
              <span
                className="font-mono text-xs"
                style={{ color: 'var(--dash-text-dim)' }}
              >
                {formatTime(event.timestamp)}
              </span>
              {elapsed !== null && elapsed > 0 && (
                <span
                  className="font-mono text-[10px]"
                  style={{ color: 'var(--dash-text-faint)' }}
                >
                  +{formatElapsed(elapsed)}
                </span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
